import {
  BookOpen,
  Camera,
  Check,
  Database,
  Globe2,
  History,
  LoaderCircle,
  Mic,
  MicOff,
  Plus,
  SendHorizontal,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';

import {
  appendMessageToNutritionLogSession,
  createNutritionLogAttachment,
  createNutritionLogSessionMessage,
  type NutritionLogAttachment,
  type NutritionLogSession,
  type NutritionLogSessionMessage,
  type NutritionLogToolActivity,
  type NutritionLogToolName,
  type NutritionLogToolStatus,
} from '@calorie-tracker/ai';
import { type ImageUploadPurpose } from '@calorie-tracker/db';
import {
  mealTypes,
  type CanonicalNutritionEntryDraft,
  type MealType,
} from '@calorie-tracker/nutrition';

import type {
  CreateManualEntryInput,
  GenerateNutritionDraftsResult,
  NutritionDraftProgressEvent,
} from '@calorie-tracker/api';

import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';

import { fieldClassName } from './constants';
import {
  formatFileSize,
  formatLocalTimestamp,
  formatMealTypeLabel,
  getErrorMessage,
} from './helpers';
import {
  createLogImageUploadErrorMessage,
  createInitialLogSession,
  createVoiceNoteFileName,
  deprecatedInitialLogAssistantText,
  getSupportedVoiceRecordingMimeType,
  readBlobAsBase64,
  readSpeechRecognitionConstructor,
  uploadFileToPresignedUrl,
} from './log-helpers';

interface ManualEntryFormState {
  calories: string;
  carbsGrams: string;
  fatGrams: string;
  mealType: MealType;
  normalizedName: string;
  proteinGrams: string;
  quantityAmount: string;
  quantityUnit: string;
}

interface LogComposerState {
  attachments: NutritionLogAttachment[];
  text: string;
}

interface RecipeDraftState {
  name: string;
  totalYieldAmount: string;
  totalYieldUnit: string;
}

type DraftReviewDecision = 'accepted' | 'rejected';

interface DraftReviewEntryState {
  calories: string;
  carbsGrams: string;
  decision?: DraftReviewDecision;
  fatGrams: string;
  mealType: MealType;
  normalizedName: string;
  proteinGrams: string;
  quantityAmount: string;
  quantityUnit: string;
}

type VoiceRecorderStatus = 'idle' | 'recording' | 'transcribing';
type LogMode = 'food' | 'recipe';

interface PersistedLogScreenState {
  activeSessionId: string | null;
  draftReviewState: Record<string, DraftReviewEntryState[]>;
  draftSuggestions: Record<string, GenerateNutritionDraftsResult>;
  logMode: LogMode;
  sessions: NutritionLogSession[];
}

interface ServerLogChatMessageAttachment {
  contentType: string;
  id: string;
  kind: 'image';
  name: string;
  size: number;
  uploadId: string;
}

interface ServerLogChatMessage {
  attachments: ServerLogChatMessageAttachment[];
  createdAt: Date | string;
  id: string;
  role: 'assistant' | 'user';
  status: NutritionLogSessionMessage['status'];
  text: string;
  updatedAt: Date | string;
}

interface ServerLogChatSession {
  activeSuggestionId: string | null;
  createdAt: Date | string;
  id: string;
  mode: LogMode;
  status: 'active' | 'archived';
  title: string;
  updatedAt: Date | string;
  userId: string;
}

interface ServerLogChatSessionDetail {
  activeSuggestion: GenerateNutritionDraftsResult | null;
  messages: ServerLogChatMessage[];
  session: ServerLogChatSession;
}

interface ServerLogChatSessionSummary extends ServerLogChatSession {
  messageCount: number;
  preview: string;
}

type DraftStreamEvent =
  | {
      data: {
        text: string;
      };
      event: 'chunk';
    }
  | {
      data: {
        message: string;
      };
      event: 'error';
    }
  | {
      data: NutritionDraftProgressEvent;
      event: 'progress';
    }
  | {
      data: GenerateNutritionDraftsResult;
      event: 'result';
    };

interface VoiceRecorderState {
  error: string | null;
  status: VoiceRecorderStatus;
}

function createDefaultManualEntryFormState(): ManualEntryFormState {
  return {
    mealType: 'breakfast',
    normalizedName: '',
    quantityAmount: '1',
    quantityUnit: 'serving',
    calories: '',
    proteinGrams: '',
    carbsGrams: '',
    fatGrams: '',
  };
}

function createDefaultLogComposerState(): LogComposerState {
  return {
    text: '',
    attachments: [],
  };
}

function createDefaultRecipeDraftState(): RecipeDraftState {
  return {
    name: '',
    totalYieldAmount: '',
    totalYieldUnit: 'g',
  };
}

function createDraftReviewEntryState(entry: CanonicalNutritionEntryDraft): DraftReviewEntryState {
  return {
    decision: 'accepted',
    mealType: entry.mealType,
    normalizedName: entry.normalizedName,
    quantityAmount: `${entry.inputQuantity.amount}`,
    quantityUnit: entry.inputQuantity.unit,
    calories: `${entry.macros.calories}`,
    proteinGrams: `${entry.macros.proteinGrams}`,
    carbsGrams: `${entry.macros.carbsGrams}`,
    fatGrams: `${entry.macros.fatGrams}`,
  };
}

function createDefaultVoiceRecorderState(): VoiceRecorderState {
  return {
    status: 'idle',
    error: null,
  };
}

function stringifyDate(value: Date | string): string {
  return value instanceof Date ? value.toISOString() : value;
}

function mapServerChatMessage(message: ServerLogChatMessage): NutritionLogSessionMessage {
  return {
    id: message.id,
    role: message.role,
    text: message.text,
    attachments: message.attachments.map((attachment) => ({
      id: attachment.id,
      kind: attachment.kind,
      name: attachment.name,
      size: attachment.size,
      contentType: attachment.contentType,
      imageUpload: {
        uploadId: attachment.uploadId,
        objectKey: '',
        purpose: 'meal-photo',
        status: 'uploaded',
      },
    })),
    createdAt: stringifyDate(message.createdAt),
    status: message.status === 'streaming' ? 'complete' : message.status,
    toolActivities: [],
  };
}

function mapServerChatDetail(detail: ServerLogChatSessionDetail): NutritionLogSession {
  return {
    id: detail.session.id,
    title: detail.session.title,
    createdAt: stringifyDate(detail.session.createdAt),
    updatedAt: stringifyDate(detail.session.updatedAt),
    messages: detail.messages.map(mapServerChatMessage),
  };
}

function mapServerChatSummary(session: ServerLogChatSessionSummary): NutritionLogSession {
  return {
    id: session.id,
    title: session.title,
    createdAt: stringifyDate(session.createdAt),
    updatedAt: stringifyDate(session.updatedAt),
    messages: [],
  };
}

function createSilentVoiceLevels(): number[] {
  return Array.from({ length: 18 }, () => 0.08);
}

function createLogSessionHash(sessionId: string): string {
  return `log/${encodeURIComponent(sessionId)}`;
}

function readLogSessionIdFromLocation(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const hash = window.location.hash.replace(/^#/, '');

  if (!hash.startsWith('log/')) {
    return null;
  }

  const encodedSessionId = hash.slice('log/'.length).trim();

  if (encodedSessionId.length === 0) {
    return null;
  }

  try {
    return decodeURIComponent(encodedSessionId);
  } catch {
    return null;
  }
}

function isNutritionLogSession(value: unknown): value is NutritionLogSession {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const session = value as Partial<NutritionLogSession>;

  return (
    typeof session.id === 'string' &&
    typeof session.title === 'string' &&
    typeof session.createdAt === 'string' &&
    typeof session.updatedAt === 'string' &&
    Array.isArray(session.messages)
  );
}

function normalizePersistedLogSession(session: NutritionLogSession): NutritionLogSession {
  return {
    ...session,
    messages: session.messages.map((message) => ({
      ...message,
      status: message.status === 'streaming' ? 'error' : (message.status ?? 'complete'),
      toolActivities: Array.isArray(message.toolActivities) ? message.toolActivities : [],
    })),
  };
}

function readPersistedLogScreenState(storageKey: string): PersistedLogScreenState {
  const fallbackSession = createInitialLogSession();

  if (typeof window === 'undefined') {
    return {
      sessions: [fallbackSession],
      activeSessionId: null,
      draftSuggestions: {},
      draftReviewState: {},
      logMode: 'food',
    };
  }

  try {
    const parsedState = JSON.parse(
      window.localStorage.getItem(storageKey) ?? 'null',
    ) as Partial<PersistedLogScreenState> | null;
    const sessions = Array.isArray(parsedState?.sessions)
      ? parsedState.sessions.filter(isNutritionLogSession).map(normalizePersistedLogSession)
      : [];

    if (sessions.length === 0) {
      return {
        sessions: [fallbackSession],
        activeSessionId: null,
        draftSuggestions: {},
        draftReviewState: {},
        logMode: 'food',
      };
    }

    return {
      sessions,
      activeSessionId:
        typeof parsedState?.activeSessionId === 'string' ? parsedState.activeSessionId : null,
      draftSuggestions:
        parsedState?.draftSuggestions && typeof parsedState.draftSuggestions === 'object'
          ? (parsedState.draftSuggestions as Record<string, GenerateNutritionDraftsResult>)
          : {},
      draftReviewState:
        parsedState?.draftReviewState && typeof parsedState.draftReviewState === 'object'
          ? (parsedState.draftReviewState as Record<string, DraftReviewEntryState[]>)
          : {},
      logMode: parsedState?.logMode === 'recipe' ? 'recipe' : 'food',
    };
  } catch {
    return {
      sessions: [fallbackSession],
      activeSessionId: null,
      draftSuggestions: {},
      draftReviewState: {},
      logMode: 'food',
    };
  }
}

function shouldStartFreshLogSessionFromLocation(): boolean {
  return readLogSessionIdFromLocation() === null;
}

function readInitialLogScreenState(storageKey: string): PersistedLogScreenState {
  const persistedState = readPersistedLogScreenState(storageKey);

  if (!shouldStartFreshLogSessionFromLocation()) {
    return persistedState;
  }

  const freshSession = createInitialLogSession();

  return {
    ...persistedState,
    activeSessionId: freshSession.id,
    sessions: [
      freshSession,
      ...persistedState.sessions.filter((session) => getSessionMessageCount(session) > 0),
    ],
  };
}

function createDraftToolActivity(input: {
  detail?: string;
  label: string;
  status: NutritionLogToolStatus;
  toolName: NutritionLogToolName;
}): NutritionLogToolActivity {
  return {
    ...(input.detail ? { detail: input.detail } : {}),
    id: input.toolName,
    label: input.label,
    status: input.status,
    toolName: input.toolName,
    updatedAt: new Date().toISOString(),
  };
}

function getSessionVisibleMessages(session: NutritionLogSession): NutritionLogSessionMessage[] {
  return session.messages.filter(
    (message, index) =>
      !(
        index === 0 &&
        message.role === 'assistant' &&
        message.text === deprecatedInitialLogAssistantText
      ),
  );
}

function getSessionMessageCount(session: NutritionLogSession): number {
  return getSessionVisibleMessages(session).length;
}

function getSessionPreview(session: NutritionLogSession): string {
  const latestVisibleMessage = [...getSessionVisibleMessages(session)]
    .reverse()
    .find((message) => message.text.length > 0 || message.attachments.length > 0);

  if (!latestVisibleMessage) {
    return 'No messages yet';
  }

  if (latestVisibleMessage.text.length > 0) {
    return latestVisibleMessage.text;
  }

  return latestVisibleMessage.attachments[0]?.name ?? 'Attachment';
}

function getLatestAssistantMessageId(session: NutritionLogSession): string | null {
  return (
    [...getSessionVisibleMessages(session)]
      .reverse()
      .find((message) => message.role === 'assistant')?.id ?? null
  );
}

function getDraftSuggestionMessageId(input: {
  session: NutritionLogSession;
  suggestion: GenerateNutritionDraftsResult;
}): string | null {
  const assistantMessageId = input.suggestion.assistantMessageId?.trim() ?? '';

  if (
    assistantMessageId.length > 0 &&
    input.session.messages.some((message) => message.id === assistantMessageId)
  ) {
    return assistantMessageId;
  }

  return getLatestAssistantMessageId(input.session);
}

function getDraftReviewStateKey(
  messageId: string,
  suggestion: GenerateNutritionDraftsResult,
): string {
  return suggestion.suggestionId ?? messageId;
}

function removeDraftSuggestionAliases(
  suggestions: Record<string, GenerateNutritionDraftsResult>,
  suggestion: GenerateNutritionDraftsResult,
): Record<string, GenerateNutritionDraftsResult> {
  const nextSuggestions = { ...suggestions };

  for (const [messageId, candidate] of Object.entries(nextSuggestions)) {
    if (
      (suggestion.suggestionId && candidate.suggestionId === suggestion.suggestionId) ||
      candidate === suggestion
    ) {
      delete nextSuggestions[messageId];
    }
  }

  return nextSuggestions;
}

function countAcceptedDraftEntries(input: {
  reviewEntries: DraftReviewEntryState[] | undefined;
  suggestion: GenerateNutritionDraftsResult;
}): number {
  return input.suggestion.draftEntries.filter(
    (_, index) => getDraftReviewEntryDecision(input.reviewEntries?.[index]) === 'accepted',
  ).length;
}

function getVisibleMessageSignature(messages: NutritionLogSessionMessage[]): string {
  return messages
    .map((message) =>
      [
        message.id,
        message.status,
        message.text.length,
        message.toolActivities
          .map((activity) => `${activity.toolName}:${activity.status}:${activity.detail ?? ''}`)
          .join(','),
      ].join(':'),
    )
    .join('|');
}

function formatSessionUpdatedAt(value: string): string {
  const updatedAt = new Date(value);

  if (Number.isNaN(updatedAt.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(updatedAt);
}

function getToolActivityIcon(toolName: NutritionLogToolName) {
  switch (toolName) {
    case 'search-food-library':
      return BookOpen;
    case 'get-logged-meal-history':
      return History;
    case 'get-usda-food-details':
    case 'search-usda-foods':
      return Database;
    case 'web-search':
      return Globe2;
    default:
      return Sparkles;
  }
}

function getDraftReviewEntryDecision(
  reviewEntry: DraftReviewEntryState | undefined,
): DraftReviewDecision {
  return reviewEntry?.decision === 'rejected' ? 'rejected' : 'accepted';
}

function parseDraftStreamEvent(rawEvent: string): DraftStreamEvent | null {
  const eventLine = rawEvent
    .split('\n')
    .find((line) => line.startsWith('event: '))
    ?.slice('event: '.length)
    .trim();
  const dataLines = rawEvent
    .split('\n')
    .filter((line) => line.startsWith('data: '))
    .map((line) => line.slice('data: '.length));

  if (!eventLine || dataLines.length === 0) {
    return null;
  }

  const data = JSON.parse(dataLines.join('\n')) as unknown;

  if (eventLine === 'chunk') {
    return {
      event: 'chunk',
      data: data as { text: string },
    };
  }

  if (eventLine === 'error') {
    return {
      event: 'error',
      data: data as { message: string },
    };
  }

  if (eventLine === 'progress') {
    return {
      event: 'progress',
      data: data as NutritionDraftProgressEvent,
    };
  }

  if (eventLine === 'result') {
    return {
      event: 'result',
      data: data as GenerateNutritionDraftsResult,
    };
  }

  return null;
}

export function LogScreen({
  onNavigateToToday,
  storageKey,
  todayDate,
}: {
  onNavigateToToday: () => void;
  storageKey: string;
  todayDate: string;
}) {
  const utils = trpc.useUtils();
  const chatSessionsQuery = trpc.log.chatSessions.useQuery({ limit: 20 });
  const createManualEntry = trpc.log.createManualEntry.useMutation();
  const createChatSession = trpc.log.createChatSession.useMutation();
  const createImageUploadUrl = trpc.log.createImageUploadUrl.useMutation();
  const completeImageUpload = trpc.log.completeImageUpload.useMutation();
  const transcribeAudio = trpc.log.transcribeAudio.useMutation();
  const confirmDraft = trpc.log.confirmDraft.useMutation();
  const saveRecipe = trpc.log.saveRecipe.useMutation();
  const recipesQuery = trpc.log.listRecipes.useQuery();
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const voiceVisualizerFrameRef = useRef<number | null>(null);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const finalTranscriptRef = useRef('');
  const activeTranscriptionIdRef = useRef<string | null>(null);
  const persistedLogStateRef = useRef<PersistedLogScreenState | null>(null);
  const readInitialLogState = () => {
    if (!persistedLogStateRef.current) {
      persistedLogStateRef.current = readInitialLogScreenState(storageKey);
    }

    return persistedLogStateRef.current;
  };
  const [sessions, setSessions] = useState<NutritionLogSession[]>(
    () => readInitialLogState().sessions,
  );
  const [activeSessionId, setActiveSessionId] = useState<string | null>(
    () => readLogSessionIdFromLocation() ?? readInitialLogState().activeSessionId,
  );
  const [composerState, setComposerState] = useState<LogComposerState>(
    createDefaultLogComposerState,
  );
  const [voiceRecorderState, setVoiceRecorderState] = useState<VoiceRecorderState>(
    createDefaultVoiceRecorderState,
  );
  const [voiceLevels, setVoiceLevels] = useState<number[]>(createSilentVoiceLevels);
  const [draftSuggestions, setDraftSuggestions] = useState<
    Record<string, GenerateNutritionDraftsResult>
  >(() => readInitialLogState().draftSuggestions);
  const [draftReviewState, setDraftReviewState] = useState<Record<string, DraftReviewEntryState[]>>(
    () => readInitialLogState().draftReviewState,
  );
  const [formState, setFormState] = useState<ManualEntryFormState>(
    createDefaultManualEntryFormState,
  );
  const [logMode, setLogMode] = useState<LogMode>(() => readInitialLogState().logMode);
  const [recipeDraftState, setRecipeDraftState] = useState<RecipeDraftState>(
    createDefaultRecipeDraftState,
  );
  const [chatError, setChatError] = useState<string | null>(null);
  const [imagePurpose] = useState<ImageUploadPurpose>('meal-photo');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [draftReviewMessageId, setDraftReviewMessageId] = useState<string | null>(null);
  const [isDraftReviewOpen, setIsDraftReviewOpen] = useState(false);
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [isRecentChatsOpen, setIsRecentChatsOpen] = useState(false);
  const activeSessionIsServerBacked =
    !!activeSessionId &&
    (chatSessionsQuery.data?.some((session) => session.id === activeSessionId) ||
      readLogSessionIdFromLocation() === activeSessionId);
  const activeServerSessionQuery = trpc.log.chatSession.useQuery(
    { sessionId: activeSessionId ?? '' },
    { enabled: activeSessionIsServerBacked },
  );

  useEffect(() => {
    const urlSessionId = readLogSessionIdFromLocation();

    if (urlSessionId) {
      if (activeSessionId !== urlSessionId) {
        setActiveSessionId(urlSessionId);
      }

      return;
    }

    if (activeSessionId !== null && sessions.some((session) => session.id === activeSessionId)) {
      return;
    }

    if (sessions[0]) {
      setActiveSessionId(sessions[0].id);
    }
  }, [activeSessionId, sessions]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        sessions,
        activeSessionId,
        draftSuggestions,
        draftReviewState,
        logMode,
      } satisfies PersistedLogScreenState),
    );
  }, [activeSessionId, draftReviewState, draftSuggestions, logMode, sessions, storageKey]);

  useEffect(() => {
    function syncActiveSessionFromHash() {
      const sessionId = readLogSessionIdFromLocation();

      if (sessionId && sessions.some((session) => session.id === sessionId)) {
        setActiveSessionId(sessionId);
      }
    }

    window.addEventListener('hashchange', syncActiveSessionFromHash);

    return () => window.removeEventListener('hashchange', syncActiveSessionFromHash);
  }, [sessions]);

  useEffect(() => {
    resizeComposerTextarea();
  }, [composerState.text]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      mediaRecorderRef.current?.stop();
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      stopVoiceVisualizer({ resetLevels: false });
    };
  }, []);

  useEffect(() => {
    if (!isDraftReviewOpen && !isManualEntryOpen && !isRecentChatsOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;

    function closeSheetOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsDraftReviewOpen(false);
        setIsManualEntryOpen(false);
        setIsRecentChatsOpen(false);
      }
    }

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeSheetOnEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', closeSheetOnEscape);
    };
  }, [isDraftReviewOpen, isManualEntryOpen, isRecentChatsOpen]);

  const requestedSessionId = readLogSessionIdFromLocation();
  const activeSession =
    sessions.find((session) => session.id === activeSessionId) ??
    (requestedSessionId ? null : (sessions[0] ?? null));
  const isDraftStreaming =
    activeSession?.messages.some((message) => message.status === 'streaming') ?? false;
  const visibleMessages = activeSession ? getSessionVisibleMessages(activeSession) : [];
  const visibleMessageSignature = getVisibleMessageSignature(visibleMessages);
  const selectedDraftMessageId =
    draftReviewMessageId && visibleMessages.some((message) => message.id === draftReviewMessageId)
      ? draftReviewMessageId
      : null;
  const activeDraftSuggestion = selectedDraftMessageId
    ? draftSuggestions[selectedDraftMessageId]
    : undefined;
  const activeDraftReviewKey =
    selectedDraftMessageId && activeDraftSuggestion
      ? getDraftReviewStateKey(selectedDraftMessageId, activeDraftSuggestion)
      : null;
  const activeAcceptedDraftCount =
    activeDraftSuggestion && activeDraftReviewKey
      ? countAcceptedDraftEntries({
          suggestion: activeDraftSuggestion,
          reviewEntries: draftReviewState[activeDraftReviewKey],
        })
      : 0;
  const currentSessionHasDraftSuggestion = visibleMessages.some(
    (message) =>
      message.role === 'assistant' &&
      !!draftSuggestions[message.id]?.suggestionId &&
      draftSuggestions[message.id]!.draftEntries.length > 0,
  );
  const recentSessions = sessions.filter((session) => {
    if (session.id === activeSession?.id) {
      return true;
    }

    return getSessionMessageCount(session) > 0;
  });

  useEffect(() => {
    if (!chatSessionsQuery.data || chatSessionsQuery.data.length === 0) {
      return;
    }

    setSessions((currentSessions) => {
      const serverSessions = chatSessionsQuery.data.map(mapServerChatSummary);
      const existingById = new Map(currentSessions.map((session) => [session.id, session]));
      const serverSessionIds = new Set(serverSessions.map((session) => session.id));
      const mergedServerSessions = serverSessions.map((serverSession) => ({
        ...serverSession,
        messages: existingById.get(serverSession.id)?.messages ?? serverSession.messages,
      }));

      return [
        ...mergedServerSessions,
        ...currentSessions.filter((session) => !serverSessionIds.has(session.id)),
      ];
    });

    if (!activeSessionId) {
      setActiveSessionId(chatSessionsQuery.data[0]?.id ?? null);
    }
  }, [activeSessionId, chatSessionsQuery.data]);

  useEffect(() => {
    if (!activeServerSessionQuery.data || isDraftStreaming) {
      return;
    }

    const serverSession = mapServerChatDetail(activeServerSessionQuery.data);

    setSessions((currentSessions) =>
      currentSessions.some((session) => session.id === serverSession.id)
        ? currentSessions.map((session) =>
            session.id === serverSession.id ? serverSession : session,
          )
        : [serverSession, ...currentSessions],
    );

    if (activeServerSessionQuery.data.activeSuggestion) {
      const messageId = getDraftSuggestionMessageId({
        session: serverSession,
        suggestion: activeServerSessionQuery.data.activeSuggestion,
      });

      if (!messageId) {
        return;
      }

      const reviewStateKey = getDraftReviewStateKey(
        messageId,
        activeServerSessionQuery.data.activeSuggestion,
      );

      setDraftSuggestions((currentState) => ({
        ...currentState,
        [messageId]: activeServerSessionQuery.data.activeSuggestion!,
      }));
      setDraftReviewState((currentState) => ({
        ...currentState,
        [reviewStateKey]:
          currentState[reviewStateKey] ??
          activeServerSessionQuery.data.activeSuggestion!.draftEntries.map(
            createDraftReviewEntryState,
          ),
      }));
    }
  }, [activeServerSessionQuery.data, isDraftStreaming]);

  useEffect(() => {
    const migrations = sessions.flatMap((session) => {
      const suggestion = draftSuggestions[session.id];

      if (!suggestion) {
        return [];
      }

      const messageId = getDraftSuggestionMessageId({ session, suggestion });

      if (!messageId || draftSuggestions[messageId]) {
        return [];
      }

      return [
        {
          messageId,
          reviewStateKey: getDraftReviewStateKey(messageId, suggestion),
          sessionId: session.id,
          suggestion,
        },
      ];
    });

    if (migrations.length === 0) {
      return;
    }

    setDraftSuggestions((currentState) => {
      const nextState = { ...currentState };

      for (const migration of migrations) {
        nextState[migration.messageId] = migration.suggestion;
        delete nextState[migration.sessionId];
      }

      return nextState;
    });
    setDraftReviewState((currentState) => {
      const nextState = { ...currentState };

      for (const migration of migrations) {
        const migratedReviewState = nextState[migration.sessionId];

        if (migratedReviewState && !nextState[migration.reviewStateKey]) {
          nextState[migration.reviewStateKey] = migratedReviewState;
        }

        delete nextState[migration.sessionId];
      }

      return nextState;
    });
  }, [draftSuggestions, sessions]);

  useEffect(() => {
    if (!activeDraftSuggestion) {
      setIsDraftReviewOpen(false);
      setDraftReviewMessageId(null);
    }
  }, [activeDraftSuggestion]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [activeSession?.id, visibleMessageSignature]);

  const mediaRecordingSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined';

  function updateFormField<Key extends keyof ManualEntryFormState>(
    key: Key,
    value: ManualEntryFormState[Key],
  ) {
    setFormState((currentState) => ({
      ...currentState,
      [key]: value,
    }));
  }

  function updateComposerField<Key extends keyof LogComposerState>(
    key: Key,
    value: LogComposerState[Key],
  ) {
    setComposerState((currentState) => ({
      ...currentState,
      [key]: value,
    }));
  }

  function resizeComposerTextarea() {
    const textarea = composerTextareaRef.current;

    if (!textarea) {
      return;
    }

    const styles = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(styles.lineHeight);
    const paddingTop = Number.parseFloat(styles.paddingTop);
    const paddingBottom = Number.parseFloat(styles.paddingBottom);
    const maxHeight =
      (Number.isFinite(lineHeight) ? lineHeight : 20) * 4 +
      (Number.isFinite(paddingTop) ? paddingTop : 0) +
      (Number.isFinite(paddingBottom) ? paddingBottom : 0);

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  function handleComposerTextChange(event: ChangeEvent<HTMLTextAreaElement>) {
    updateComposerField('text', event.target.value);
  }

  function updateRecipeDraftField<Key extends keyof RecipeDraftState>(
    key: Key,
    value: RecipeDraftState[Key],
  ) {
    setRecipeDraftState((currentState) => ({
      ...currentState,
      [key]: value,
    }));
  }

  function updateDraftReviewEntryField<Key extends keyof DraftReviewEntryState>(
    reviewStateKey: string,
    entryIndex: number,
    key: Key,
    value: DraftReviewEntryState[Key],
  ) {
    setDraftReviewState((currentState) => ({
      ...currentState,
      [reviewStateKey]: (
        currentState[reviewStateKey] ??
        activeDraftSuggestion?.draftEntries.map(createDraftReviewEntryState) ??
        []
      ).map((entry, index) => (index === entryIndex ? { ...entry, [key]: value } : entry)),
    }));
  }

  function updateDraftReviewEntryDecision(
    reviewStateKey: string,
    entryIndex: number,
    decision: DraftReviewDecision,
  ) {
    updateDraftReviewEntryField(reviewStateKey, entryIndex, 'decision', decision);
  }

  function removeComposerAttachment(attachmentId: string) {
    setComposerState((currentState) => ({
      ...currentState,
      attachments: currentState.attachments.filter((attachment) => attachment.id !== attachmentId),
    }));
  }

  function appendMessageToSession(sessionId: string, message: NutritionLogSessionMessage) {
    setSessions((currentSessions) =>
      currentSessions.map((session) => {
        if (session.id !== sessionId) {
          return session;
        }

        return appendMessageToNutritionLogSession(session, message);
      }),
    );
  }

  function updateSessionMessage(
    sessionId: string,
    messageId: string,
    updater: (message: NutritionLogSessionMessage) => NutritionLogSessionMessage,
  ) {
    setSessions((currentSessions) =>
      currentSessions.map((session) => {
        if (session.id !== sessionId) {
          return session;
        }

        return {
          ...session,
          updatedAt: new Date().toISOString(),
          messages: session.messages.map((message) =>
            message.id === messageId ? updater(message) : message,
          ),
        };
      }),
    );
  }

  function navigateToSession(sessionId: string) {
    setActiveSessionId(sessionId);
    setChatError(null);
    setIsRecentChatsOpen(false);

    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${createLogSessionHash(sessionId)}`);
    }
  }

  async function ensureServerSession(session: NutritionLogSession): Promise<NutritionLogSession> {
    if (
      chatSessionsQuery.data?.some((serverSession) => serverSession.id === session.id) ||
      activeServerSessionQuery.data?.session.id === session.id
    ) {
      return session;
    }

    const serverSession = await createChatSession.mutateAsync({ mode: logMode });
    const migratedSession: NutritionLogSession = {
      ...session,
      id: serverSession.id,
      title: serverSession.title,
      createdAt: stringifyDate(serverSession.createdAt),
      updatedAt: stringifyDate(serverSession.updatedAt),
    };

    setSessions((currentSessions) =>
      currentSessions.map((candidate) =>
        candidate.id === session.id ? migratedSession : candidate,
      ),
    );
    setActiveSessionId(serverSession.id);

    return migratedSession;
  }

  function updateAssistantToolActivity(
    sessionId: string,
    messageId: string,
    input: {
      detail?: string;
      label: string;
      status: NutritionLogToolStatus;
      toolName: NutritionLogToolName;
    },
  ) {
    updateSessionMessage(sessionId, messageId, (message) => {
      const nextActivity = createDraftToolActivity(input);
      const existingActivityIndex = message.toolActivities.findIndex(
        (activity) => activity.toolName === input.toolName,
      );
      const toolActivities =
        existingActivityIndex >= 0
          ? message.toolActivities.map((activity, index) =>
              index === existingActivityIndex ? nextActivity : activity,
            )
          : [...message.toolActivities, nextActivity];

      return {
        ...message,
        toolActivities,
      };
    });
  }

  async function requestDraftStream(input: {
    assistantMessageId: string;
    attachments: NutritionLogAttachment[];
    requestText: string;
    sessionId: string;
    userMessage: NutritionLogSessionMessage;
  }): Promise<GenerateNutritionDraftsResult> {
    const response = await fetch('/api/log/generate-drafts-stream', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        attachments: input.attachments,
        mode: logMode,
        sessionId: input.sessionId,
        submittedAt: formatLocalTimestamp(new Date()),
        ...(input.requestText.trim().length > 0 ? { text: input.requestText } : {}),
      }),
    });

    if (!response.ok || !response.body) {
      throw new Error(`Draft generation failed with status ${response.status}.`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: GenerateNutritionDraftsResult | null = null;

    const processRawEvent = (rawEvent: string) => {
      const event = parseDraftStreamEvent(rawEvent);

      if (!event) {
        return;
      }

      if (event.event === 'progress') {
        updateAssistantToolActivity(input.sessionId, input.assistantMessageId, {
          ...(event.data.detail ? { detail: event.data.detail } : {}),
          label: event.data.label,
          status:
            event.data.status === 'failed'
              ? 'failed'
              : event.data.status === 'completed'
                ? 'completed'
                : 'running',
          toolName: event.data.toolName,
        });
      }

      if (event.event === 'chunk') {
        updateSessionMessage(input.sessionId, input.assistantMessageId, (message) => ({
          ...message,
          text: `${message.text}${event.data.text}`,
        }));
      }

      if (event.event === 'result') {
        result = event.data;
      }

      if (event.event === 'error') {
        throw new Error(event.data.message);
      }
    };

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const rawEvents = buffer.split('\n\n');
      buffer = rawEvents.pop() ?? '';

      for (const rawEvent of rawEvents) {
        processRawEvent(rawEvent);
      }
    }

    buffer += decoder.decode();

    if (buffer.trim().length > 0) {
      processRawEvent(buffer);
    }

    if (!result) {
      throw new Error('The draft stream ended before returning a result.');
    }

    return result;
  }

  function appendVoiceTranscriptToComposer(transcript: string) {
    const normalizedTranscript = transcript.trim();

    if (normalizedTranscript.length === 0) {
      return;
    }

    setComposerState((currentState) => ({
      ...currentState,
      text:
        currentState.text.trim().length > 0
          ? `${currentState.text.trim()}\n\n${normalizedTranscript}`
          : normalizedTranscript,
    }));
    window.requestAnimationFrame(() => {
      composerTextareaRef.current?.focus();
      resizeComposerTextarea();
    });
    setChatError(null);
  }

  function startVoiceVisualizer(stream: MediaStream) {
    type AudioContextConstructor = new () => AudioContext;
    const AudioContextCtor =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: AudioContextConstructor })
        .webkitAudioContext;

    if (!AudioContextCtor) {
      setVoiceLevels(createSilentVoiceLevels());
      return;
    }

    const audioContext = new AudioContextCtor();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.82;

    const source = audioContext.createMediaStreamSource(stream);
    const frequencyData = new Uint8Array(analyser.frequencyBinCount);

    source.connect(analyser);
    audioContextRef.current = audioContext;
    audioSourceRef.current = source;

    function drawVoiceLevels() {
      analyser.getByteFrequencyData(frequencyData);

      const barCount = 18;
      const bucketSize = Math.max(1, Math.floor(frequencyData.length / barCount));
      const nextLevels = Array.from({ length: barCount }, (_, barIndex) => {
        let sum = 0;
        let count = 0;
        const start = barIndex * bucketSize;
        const end = Math.min(frequencyData.length, start + bucketSize);

        for (let index = start; index < end; index += 1) {
          sum += frequencyData[index] ?? 0;
          count += 1;
        }

        const average = count > 0 ? sum / count : 0;

        return Math.max(0.08, Math.min(1, average / 150));
      });

      setVoiceLevels(nextLevels);
      voiceVisualizerFrameRef.current = window.requestAnimationFrame(drawVoiceLevels);
    }

    drawVoiceLevels();
  }

  function stopVoiceVisualizer({ resetLevels = true }: { resetLevels?: boolean } = {}) {
    if (voiceVisualizerFrameRef.current !== null) {
      window.cancelAnimationFrame(voiceVisualizerFrameRef.current);
      voiceVisualizerFrameRef.current = null;
    }

    audioSourceRef.current?.disconnect();
    audioSourceRef.current = null;
    void audioContextRef.current?.close();
    audioContextRef.current = null;

    if (resetLevels) {
      setVoiceLevels(createSilentVoiceLevels());
    }
  }

  async function requestVoiceTranscript(input: {
    audioBlob: Blob;
    browserTranscript: string;
    contentType: string;
    fileName: string;
    transcriptionId: string;
  }) {
    const browserTranscript = input.browserTranscript.trim();

    setVoiceRecorderState((currentState) => {
      if (activeTranscriptionIdRef.current !== input.transcriptionId) {
        return currentState;
      }

      return {
        ...currentState,
        error: null,
        status: 'transcribing',
      };
    });

    try {
      const result = await transcribeAudio.mutateAsync({
        audioBase64: await readBlobAsBase64(input.audioBlob),
        browserTranscript,
        contentType: input.contentType,
        fileName: input.fileName,
        language: 'en',
      });

      if (activeTranscriptionIdRef.current !== input.transcriptionId) {
        return;
      }

      const transcript = result.transcript.trim() || browserTranscript;

      if (transcript.length === 0) {
        activeTranscriptionIdRef.current = null;
        setVoiceRecorderState({
          error: 'The recording finished, but no transcript was detected.',
          status: 'idle',
        });
        return;
      }

      appendVoiceTranscriptToComposer(transcript);
      activeTranscriptionIdRef.current = null;
      setVoiceRecorderState(createDefaultVoiceRecorderState());
    } catch (error) {
      if (activeTranscriptionIdRef.current !== input.transcriptionId) {
        return;
      }

      if (browserTranscript.length > 0) {
        appendVoiceTranscriptToComposer(browserTranscript);
        activeTranscriptionIdRef.current = null;
        setVoiceRecorderState(createDefaultVoiceRecorderState());
        return;
      }

      activeTranscriptionIdRef.current = null;
      setVoiceRecorderState({
        error: getErrorMessage(error),
        status: 'idle',
      });
    }
  }

  function startNewSession() {
    const session = createInitialLogSession();

    setSessions((currentSessions) => [
      session,
      ...currentSessions.filter((currentSession) => getSessionMessageCount(currentSession) > 0),
    ]);
    setActiveSessionId(session.id);
    setComposerState(createDefaultLogComposerState());
    setChatError(null);
    setIsRecentChatsOpen(false);

    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', '#log');
    }
  }

  async function handleAttachmentSelection(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);

    if (files.length === 0) {
      return;
    }
    event.target.value = '';

    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      setChatError('Only meal photos and food label images can be attached here right now.');
    }

    if (imageFiles.length === 0) {
      return;
    }

    try {
      for (const file of imageFiles) {
        const uploadTarget = await createImageUploadUrl.mutateAsync({
          contentType: file.type || 'image/jpeg',
          fileName: file.name,
          fileSize: file.size,
          purpose: imagePurpose,
        });
        await uploadFileToPresignedUrl({
          file,
          contentType: uploadTarget.contentType,
          headers: uploadTarget.headers,
          method: uploadTarget.method,
          uploadUrl: uploadTarget.uploadUrl,
        });
        const result = await completeImageUpload.mutateAsync({
          contentType: uploadTarget.contentType,
          fileName: uploadTarget.fileName,
          objectKey: uploadTarget.objectKey,
          purpose: uploadTarget.purpose,
          uploadId: uploadTarget.uploadId,
        });

        const attachment = createNutritionLogAttachment({
          contentType: result.upload.contentType,
          name: result.upload.originalFileName,
          size: result.upload.byteSize,
          imageUpload: {
            uploadId: result.upload.id,
            objectKey: result.upload.r2ObjectKey,
            purpose: result.upload.purpose,
            status: result.upload.status,
            bucketName: result.upload.bucketName,
          },
        });

        setComposerState((currentState) => ({
          ...currentState,
          attachments: [...currentState.attachments, attachment],
        }));
      }

      setChatError(null);
    } catch (error) {
      setChatError(createLogImageUploadErrorMessage(error));
    }
  }

  async function startVoiceRecording() {
    if (!mediaRecordingSupported) {
      setVoiceRecorderState((currentState) => ({
        ...currentState,
        error: 'This browser does not support microphone recording.',
      }));
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const SpeechRecognitionConstructor = readSpeechRecognitionConstructor();
      const supportedRecordingMimeType = getSupportedVoiceRecordingMimeType();
      const mediaRecorder = supportedRecordingMimeType
        ? new MediaRecorder(stream, { mimeType: supportedRecordingMimeType })
        : new MediaRecorder(stream);

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      finalTranscriptRef.current = '';
      activeTranscriptionIdRef.current = null;
      recognitionRef.current?.stop();
      stopVoiceVisualizer();
      startVoiceVisualizer(stream);

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const now = new Date();
        const audioContentType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: audioContentType });
        const fileName = createVoiceNoteFileName(now, audioContentType);
        const browserTranscript = finalTranscriptRef.current.trim();
        const transcriptionId = crypto.randomUUID();

        activeTranscriptionIdRef.current = transcriptionId;
        setVoiceRecorderState({
          status: 'transcribing',
          error: null,
        });
        mediaRecorderRef.current = null;
        stopVoiceVisualizer();
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        void requestVoiceTranscript({
          audioBlob,
          browserTranscript,
          contentType: audioContentType,
          fileName,
          transcriptionId,
        });
      };

      if (SpeechRecognitionConstructor) {
        const recognition = new SpeechRecognitionConstructor();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onresult = (event) => {
          let finalTranscript = '';
          let interimTranscript = '';

          for (let index = 0; index < event.results.length; index += 1) {
            const result = event.results[index];

            if (!result) {
              continue;
            }

            const alternative = result[0];

            if (!alternative) {
              continue;
            }

            if (result.isFinal) {
              finalTranscript += `${alternative.transcript} `;
            } else {
              interimTranscript += `${alternative.transcript} `;
            }
          }

          finalTranscriptRef.current = [finalTranscript.trim(), interimTranscript.trim()]
            .filter((part) => part.length > 0)
            .join(' ');
        };
        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
          setVoiceRecorderState((currentState) => ({
            ...currentState,
            error:
              event.error === 'not-allowed'
                ? 'Microphone access was denied.'
                : 'Live transcription ran into a browser error. The server transcription will still run when you stop recording.',
          }));
        };
        recognition.onend = () => {
          recognitionRef.current = null;
        };
        recognitionRef.current = recognition;
        recognition.start();
      }

      mediaRecorder.start();
      setVoiceRecorderState({
        status: 'recording',
        error: null,
      });
    } catch (error) {
      stopVoiceVisualizer();
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
      setVoiceRecorderState((currentState) => ({
        ...currentState,
        error: getErrorMessage(error),
      }));
    }
  }

  function stopVoiceRecording() {
    recognitionRef.current?.stop();
    recognitionRef.current = null;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }

  async function handleChatSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!activeSession) {
      setChatError('Start a new log session before sending a message.');
      return;
    }

    const hasText = composerState.text.trim().length > 0;
    const hasAttachments = composerState.attachments.length > 0;

    if (!hasText && !hasAttachments) {
      setChatError('Add a message or attachment before sending.');
      return;
    }

    let serverBackedSession: NutritionLogSession;

    try {
      serverBackedSession = await ensureServerSession(activeSession);
    } catch (error) {
      setChatError(getErrorMessage(error));
      return;
    }

    const userMessage = createNutritionLogSessionMessage({
      role: 'user',
      text: composerState.text,
      attachments: composerState.attachments,
    });
    const sessionId = serverBackedSession.id;
    const draftRequestAttachments = userMessage.attachments;

    appendMessageToSession(sessionId, userMessage);
    setComposerState(createDefaultLogComposerState());
    setChatError(null);

    let assistantMessage: NutritionLogSessionMessage | null = null;

    try {
      if (!assistantMessage) {
        assistantMessage = {
          ...createNutritionLogSessionMessage({
            role: 'assistant',
            text: '',
          }),
          status: 'streaming',
          toolActivities: [],
        };
        appendMessageToSession(sessionId, assistantMessage);
      }

      if (!assistantMessage) {
        throw new Error('The assistant response could not be prepared.');
      }

      const currentAssistantMessage = assistantMessage;
      const result = await requestDraftStream({
        assistantMessageId: currentAssistantMessage.id,
        attachments: draftRequestAttachments,
        requestText: userMessage.text,
        sessionId,
        userMessage,
      });

      updateSessionMessage(sessionId, currentAssistantMessage.id, (message) => ({
        ...message,
        status: 'complete',
        text:
          message.text.trim().length === 0
            ? result.message
            : message.text.includes(result.message)
              ? message.text
              : `${message.text.trimEnd()}\n\n${result.message}`,
      }));
      const reviewStateKey = getDraftReviewStateKey(currentAssistantMessage.id, result);
      const draftMessageIds = Array.from(
        new Set(
          [currentAssistantMessage.id, result.assistantMessageId?.trim()].filter(
            (messageId): messageId is string => !!messageId && messageId.length > 0,
          ),
        ),
      );

      setDraftSuggestions((currentState) => {
        const nextState = { ...currentState };

        for (const messageId of draftMessageIds) {
          nextState[messageId] = result;
        }

        return nextState;
      });
      setDraftReviewState((currentState) => ({
        ...currentState,
        [reviewStateKey]: result.draftEntries.map(createDraftReviewEntryState),
      }));
      setIsDraftReviewOpen(false);
      await Promise.all([chatSessionsQuery.refetch(), activeServerSessionQuery.refetch()]);
    } catch (error) {
      const message = getErrorMessage(error);

      if (assistantMessage) {
        updateSessionMessage(sessionId, assistantMessage.id, (currentMessage) => ({
          ...currentMessage,
          status: 'error',
          text:
            currentMessage.text.length > 0
              ? `${currentMessage.text}\nI could not finish that draft. ${message}`
              : `I could not draft that yet. ${message}`,
        }));
      } else {
        appendMessageToSession(sessionId, {
          ...createNutritionLogSessionMessage({
            role: 'assistant',
            text: `I could not draft that yet. ${message}`,
          }),
          status: 'error',
        });
      }
      setChatError(message);
    }
  }

  async function handleManualEntrySubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);

    const quantityAmount = Number(formState.quantityAmount);
    const calories = Number(formState.calories);
    const proteinGrams = Number(formState.proteinGrams);
    const carbsGrams = Number(formState.carbsGrams);
    const fatGrams = Number(formState.fatGrams);

    if (
      !Number.isFinite(quantityAmount) ||
      !Number.isFinite(calories) ||
      !Number.isFinite(proteinGrams) ||
      !Number.isFinite(carbsGrams) ||
      !Number.isFinite(fatGrams)
    ) {
      setSubmitError('Enter valid numbers for quantity and macros before saving.');
      return;
    }

    const consumedAt = formatLocalTimestamp(new Date());
    const input: CreateManualEntryInput = {
      consumedAt,
      mealType: formState.mealType,
      normalizedName: formState.normalizedName,
      quantity: {
        amount: quantityAmount,
        unit: formState.quantityUnit,
      },
      macros: {
        calories,
        proteinGrams,
        carbsGrams,
        fatGrams,
      },
    };

    try {
      const result = await createManualEntry.mutateAsync(input);
      const consumedOn = result.meal.consumedOn;

      await Promise.all([
        utils.today.summary.invalidate({ date: consumedOn }),
        utils.history.days.invalidate({
          beforeDate: todayDate,
          limit: 14,
        }),
      ]);

      setFormState({
        ...createDefaultManualEntryFormState(),
        mealType: formState.mealType,
      });
      setSubmitSuccess(
        `${result.entry.normalizedName} saved to ${formatMealTypeLabel(result.meal.mealType)}.`,
      );
      setIsManualEntryOpen(false);
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  }

  async function handleConfirmDraft() {
    if (
      !selectedDraftMessageId ||
      !activeDraftReviewKey ||
      !activeDraftSuggestion ||
      !activeDraftSuggestion.suggestionId
    ) {
      return;
    }

    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const reviewedDrafts = buildReviewedDraftEntries({ acceptedOnly: true });

      if (reviewedDrafts.length === 0) {
        throw new Error('Accept at least one draft food before saving.');
      }

      const result = await confirmDraft.mutateAsync({
        suggestionId: activeDraftSuggestion.suggestionId,
        entries: reviewedDrafts,
      });

      await Promise.all([
        utils.today.summary.invalidate({ date: result.date }),
        utils.history.days.invalidate({
          beforeDate: todayDate,
          limit: 14,
        }),
      ]);

      setDraftSuggestions((currentState) => {
        return removeDraftSuggestionAliases(currentState, activeDraftSuggestion);
      });
      setDraftReviewState((currentState) => {
        const nextState = { ...currentState };
        delete nextState[activeDraftReviewKey];
        return nextState;
      });
      setSubmitSuccess(
        `${result.entries.length} draft entr${result.entries.length === 1 ? 'y' : 'ies'} saved.`,
      );
      setIsDraftReviewOpen(false);
      onNavigateToToday();
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  }

  function buildReviewedDraftEntries({ acceptedOnly = false }: { acceptedOnly?: boolean } = {}) {
    if (!activeDraftSuggestion || !activeDraftReviewKey) {
      return [];
    }

    const reviewEntries = draftReviewState[activeDraftReviewKey] ?? [];

    return activeDraftSuggestion.draftEntries
      .map((entry, index) => {
        const reviewEntry = reviewEntries[index];

        if (!reviewEntry) {
          return entry;
        }

        if (acceptedOnly && getDraftReviewEntryDecision(reviewEntry) === 'rejected') {
          return null;
        }

        const quantityAmount = Number(reviewEntry.quantityAmount);
        const calories = Number(reviewEntry.calories);
        const proteinGrams = Number(reviewEntry.proteinGrams);
        const carbsGrams = Number(reviewEntry.carbsGrams);
        const fatGrams = Number(reviewEntry.fatGrams);

        if (
          !Number.isFinite(quantityAmount) ||
          !Number.isFinite(calories) ||
          !Number.isFinite(proteinGrams) ||
          !Number.isFinite(carbsGrams) ||
          !Number.isFinite(fatGrams)
        ) {
          throw new Error('Enter valid numbers for every accepted draft before saving.');
        }

        return {
          ...entry,
          mealType: reviewEntry.mealType,
          normalizedName: reviewEntry.normalizedName,
          inputQuantity: {
            amount: quantityAmount,
            unit: reviewEntry.quantityUnit,
          },
          canonicalQuantity: {
            amount: quantityAmount,
            unit: reviewEntry.quantityUnit,
          },
          macros: {
            ...entry.macros,
            calories,
            proteinGrams,
            carbsGrams,
            fatGrams,
          },
        };
      })
      .filter((entry): entry is CanonicalNutritionEntryDraft => entry !== null);
  }

  async function handleSaveRecipe() {
    if (!selectedDraftMessageId || !activeDraftReviewKey || !activeDraftSuggestion) {
      return;
    }

    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const totalYieldAmount = Number(recipeDraftState.totalYieldAmount);

      if (!Number.isFinite(totalYieldAmount) || totalYieldAmount <= 0) {
        throw new Error('Enter the finished recipe weight in grams before saving.');
      }

      const ingredients = buildReviewedDraftEntries({ acceptedOnly: true });

      if (ingredients.length === 0) {
        throw new Error('Accept at least one draft ingredient before saving the recipe.');
      }

      const result = await saveRecipe.mutateAsync({
        name: recipeDraftState.name,
        totalYield: {
          amount: totalYieldAmount,
          unit: recipeDraftState.totalYieldUnit,
        },
        ingredients,
      });

      await recipesQuery.refetch();

      setDraftSuggestions((currentState) => {
        return removeDraftSuggestionAliases(currentState, activeDraftSuggestion);
      });
      setDraftReviewState((currentState) => {
        const nextState = { ...currentState };
        delete nextState[activeDraftReviewKey];
        return nextState;
      });
      setRecipeDraftState(createDefaultRecipeDraftState());
      setSubmitSuccess(
        `${result.recipe.name} saved with ${result.ingredientCount} ingredient${
          result.ingredientCount === 1 ? '' : 's'
        }.`,
      );
      setIsDraftReviewOpen(false);
      setLogMode('food');
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <h2 className="text-lg font-semibold">Log</h2>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            onClick={() => setIsRecentChatsOpen(true)}
            size="sm"
            type="button"
            variant="outline"
          >
            <History className="h-4 w-4" />
            Recent
          </Button>
          <Button
            onClick={() => {
              setLogMode((currentMode) => (currentMode === 'recipe' ? 'food' : 'recipe'));
              setChatError(null);
            }}
            size="sm"
            type="button"
            variant={logMode === 'recipe' ? 'default' : 'outline'}
          >
            <BookOpen className="h-4 w-4" />
            Recipe
          </Button>
          <Button
            onClick={() => setIsManualEntryOpen(true)}
            size="sm"
            type="button"
            variant="outline"
          >
            Manual
          </Button>
          <Button
            disabled={createChatSession.isPending}
            onClick={() => void startNewSession()}
            size="sm"
            type="button"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            New
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        {logMode === 'recipe' ? (
          <div className="rounded-md border border-border bg-background px-3 py-2">
            <p className="text-sm font-medium">Recipe draft</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Add ingredients in chat or by voice, then save the finished weight in grams.
            </p>
          </div>
        ) : recipesQuery.data && recipesQuery.data.length > 0 ? (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {recipesQuery.data.slice(0, 8).map((summary) => (
              <span
                className="shrink-0 rounded-md border border-border/70 bg-background px-2 py-1 text-xs text-muted-foreground"
                key={summary.recipe.id}
              >
                {summary.recipe.name}
              </span>
            ))}
          </div>
        ) : null}

        {visibleMessages.length > 0 ? (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pb-3">
            {visibleMessages.map((message: NutritionLogSessionMessage) => {
              const isUser = message.role === 'user';
              const messageDraftSuggestion = isUser ? undefined : draftSuggestions[message.id];
              const messageDraftReviewKey = messageDraftSuggestion
                ? getDraftReviewStateKey(message.id, messageDraftSuggestion)
                : null;
              const messageAcceptedDraftCount =
                messageDraftSuggestion && messageDraftReviewKey
                  ? countAcceptedDraftEntries({
                      suggestion: messageDraftSuggestion,
                      reviewEntries: draftReviewState[messageDraftReviewKey],
                    })
                  : 0;
              const shouldShowDraftReviewActions =
                !isUser &&
                !!messageDraftSuggestion &&
                !!messageDraftSuggestion.suggestionId &&
                messageDraftSuggestion.draftEntries.length > 0;

              return (
                <div
                  className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                  key={message.id}
                >
                  <div
                    className={`max-w-[88%] rounded-lg px-3 py-2 ${
                      isUser
                        ? 'bg-foreground text-background'
                        : 'border border-border/70 bg-card text-foreground'
                    }`}
                  >
                    <p className="text-xs font-medium opacity-70">{isUser ? 'You' : 'Assistant'}</p>
                    {message.text.length > 0 ? (
                      <p className="mt-2 whitespace-pre-line text-sm leading-6">{message.text}</p>
                    ) : message.status === 'streaming' ? (
                      <p className="mt-2 inline-flex items-center gap-2 text-sm leading-6 text-muted-foreground">
                        <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                        Working on it
                      </p>
                    ) : null}
                    {!isUser && message.toolActivities.length > 0 ? (
                      <div className="mt-3 space-y-1">
                        {message.toolActivities.map((activity) => {
                          const Icon = getToolActivityIcon(activity.toolName);

                          return (
                            <div
                              className="flex items-start gap-2 rounded-md bg-secondary/70 px-2 py-1.5 text-xs text-secondary-foreground"
                              key={activity.id}
                            >
                              {activity.status === 'running' ? (
                                <LoaderCircle className="mt-0.5 h-3.5 w-3.5 animate-spin" />
                              ) : activity.status === 'failed' ? (
                                <X className="mt-0.5 h-3.5 w-3.5" />
                              ) : (
                                <Check className="mt-0.5 h-3.5 w-3.5" />
                              )}
                              <Icon className="mt-0.5 h-3.5 w-3.5 opacity-70" />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate">{activity.label}</span>
                                {activity.detail ? (
                                  <span className="mt-0.5 block truncate text-[11px] opacity-70">
                                    {activity.detail}
                                  </span>
                                ) : null}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    {message.attachments.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {message.attachments.map((attachment) => (
                          <span
                            className={`rounded px-2 py-1 text-xs ${
                              isUser
                                ? 'bg-background/15 text-background'
                                : 'bg-secondary text-secondary-foreground'
                            }`}
                            key={attachment.id}
                          >
                            {attachment.kind} • {attachment.name} •{' '}
                            {formatFileSize(attachment.size)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {shouldShowDraftReviewActions ? (
                      <div className="mt-3 rounded-md border border-border/70 bg-background p-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium">
                              {logMode === 'recipe' ? 'Recipe draft' : 'Food draft'}
                            </span>
                            <span className="mt-0.5 block text-xs text-muted-foreground">
                              {messageDraftSuggestion?.draftEntries.length ?? 0} item
                              {messageDraftSuggestion?.draftEntries.length === 1 ? '' : 's'} ready
                              {messageAcceptedDraftCount !==
                              messageDraftSuggestion!.draftEntries.length
                                ? ` • ${messageAcceptedDraftCount} accepted`
                                : ''}
                            </span>
                          </span>
                          <Button
                            onClick={() => {
                              setDraftReviewMessageId(message.id);
                              setIsDraftReviewOpen(true);
                            }}
                            size="sm"
                            type="button"
                            variant="default"
                          >
                            Review
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="min-h-0 flex-1" ref={messagesEndRef} />
        )}

        {voiceRecorderState.status !== 'idle' ? (
          <div className="flex shrink-0 items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
            {voiceRecorderState.status === 'recording' ? (
              <>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.14)]" />
                <div
                  aria-hidden="true"
                  className="flex h-8 flex-1 items-center gap-1 overflow-hidden"
                >
                  {voiceLevels.map((level, index) => (
                    <span
                      className="h-full w-1 flex-1 origin-center rounded-full bg-primary/70 transition-transform duration-75 ease-out motion-reduce:transition-none"
                      key={`voice-level-${index}`}
                      style={{ transform: `scaleY(${Math.max(0.08, level)})` }}
                    />
                  ))}
                </div>
                <Button
                  aria-label="Stop recording"
                  className="h-8 w-8 shrink-0"
                  onClick={stopVoiceRecording}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <MicOff className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                <span className="min-w-0 flex-1 text-sm text-muted-foreground">
                  Transcribing...
                </span>
              </>
            )}
          </div>
        ) : voiceRecorderState.error ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {voiceRecorderState.error}
          </p>
        ) : null}

        <form
          className="shrink-0 space-y-2 bg-background pb-[calc(env(safe-area-inset-bottom)+0.75rem)]"
          onSubmit={handleChatSubmit}
        >
          <div className="flex flex-col gap-1.5 rounded-lg border border-input bg-background p-1.5">
            <textarea
              className="max-h-[6rem] min-h-10 w-full resize-none overflow-hidden bg-transparent px-2 py-2 text-base leading-5 outline-none placeholder:text-muted-foreground sm:text-sm"
              onChange={handleComposerTextChange}
              placeholder="What did you eat?"
              ref={composerTextareaRef}
              rows={1}
              value={composerState.text}
            />

            <div className="flex items-center justify-between gap-1.5">
              <div className="flex items-center gap-1.5">
                <label
                  className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  title="Attach image"
                >
                  {createImageUploadUrl.isPending || completeImageUpload.isPending ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                  <span className="sr-only">Attach image</span>
                  <input
                    className="sr-only"
                    accept="image/*"
                    disabled={createImageUploadUrl.isPending || completeImageUpload.isPending}
                    multiple
                    onChange={(event) => void handleAttachmentSelection(event)}
                    type="file"
                  />
                </label>

                <Button
                  aria-label={
                    voiceRecorderState.status === 'recording'
                      ? 'Stop recording'
                      : voiceRecorderState.status === 'transcribing'
                        ? 'Transcribing voice note'
                        : 'Record voice note'
                  }
                  className="h-10 w-10 shrink-0"
                  disabled={
                    !mediaRecordingSupported || voiceRecorderState.status === 'transcribing'
                  }
                  onClick={
                    voiceRecorderState.status === 'recording'
                      ? stopVoiceRecording
                      : () => void startVoiceRecording()
                  }
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  {voiceRecorderState.status === 'recording' ? (
                    <MicOff className="h-4 w-4" />
                  ) : voiceRecorderState.status === 'transcribing' ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <Button
                aria-label="Send to log"
                className="h-10 w-10 shrink-0"
                disabled={
                  createImageUploadUrl.isPending ||
                  completeImageUpload.isPending ||
                  isDraftStreaming
                }
                size="icon"
                type="submit"
              >
                {isDraftStreaming ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <SendHorizontal className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {composerState.attachments.map((attachment) => (
              <button
                className="inline-flex items-center gap-2 rounded border border-border/70 bg-background px-2 py-1 text-xs text-muted-foreground"
                key={attachment.id}
                onClick={() => removeComposerAttachment(attachment.id)}
                type="button"
              >
                <span>
                  {attachment.kind} • {attachment.name} • {formatFileSize(attachment.size)}
                </span>
                <X className="h-3 w-3" />
              </button>
            ))}
          </div>

          {chatError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {chatError}
            </p>
          ) : null}
        </form>
      </div>

      {isDraftReviewOpen && activeDraftSuggestion ? (
        <div
          aria-modal="true"
          className="fixed inset-0 z-50 flex justify-end bg-foreground/30 side-sheet-backdrop"
          role="dialog"
        >
          <button
            aria-label="Close draft review"
            className="absolute inset-0 h-full w-full cursor-default"
            onClick={() => setIsDraftReviewOpen(false)}
            type="button"
          />
          <section className="side-sheet-panel relative h-full w-full max-w-xl overflow-y-auto border-l border-border bg-card px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-4 shadow-[-12px_0_40px_rgba(0,0,0,0.18)]">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Review</p>
                <h3 className="mt-1 text-xl font-semibold">
                  {logMode === 'recipe' ? 'Recipe draft' : 'Food draft'}
                </h3>
              </div>
              <Button
                aria-label="Close draft review"
                onClick={() => setIsDraftReviewOpen(false)}
                size="icon"
                type="button"
                variant="outline"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {logMode === 'recipe' ? (
                <div className="grid gap-3 rounded-md border border-border/70 bg-background p-3">
                  <label className="grid gap-2 text-sm">
                    <span className="font-medium">Recipe name</span>
                    <input
                      className={fieldClassName}
                      onChange={(event) => updateRecipeDraftField('name', event.target.value)}
                      placeholder="Beef bowl"
                      value={recipeDraftState.name}
                    />
                  </label>
                  <div className="grid grid-cols-[1fr_0.7fr] gap-3">
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Finished weight</span>
                      <input
                        className={fieldClassName}
                        inputMode="decimal"
                        onChange={(event) =>
                          updateRecipeDraftField('totalYieldAmount', event.target.value)
                        }
                        placeholder="1240"
                        value={recipeDraftState.totalYieldAmount}
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="font-medium">Unit</span>
                      <input
                        className={fieldClassName}
                        onChange={(event) =>
                          updateRecipeDraftField('totalYieldUnit', event.target.value)
                        }
                        value={recipeDraftState.totalYieldUnit}
                      />
                    </label>
                  </div>
                </div>
              ) : null}

              {activeDraftSuggestion.clarificationQuestion ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {activeDraftSuggestion.clarificationQuestion}
                </p>
              ) : null}

              {activeDraftSuggestion.draftEntries.length === 0 ? (
                <p className="rounded-md border border-border/70 bg-background px-3 py-2 text-sm text-muted-foreground">
                  No reviewable entries yet. Add a clearer note or image and send again.
                </p>
              ) : (
                <div className="divide-y divide-border border-y border-border">
                  {activeDraftSuggestion.draftEntries.map((entry, index) => {
                    const reviewEntry = activeDraftReviewKey
                      ? draftReviewState[activeDraftReviewKey]?.[index]
                      : undefined;
                    const decision = getDraftReviewEntryDecision(reviewEntry);
                    const isRejected = decision === 'rejected';

                    return (
                      <div
                        className={`space-y-3 py-4 ${isRejected ? 'opacity-60' : ''}`}
                        key={`${activeDraftSuggestion.suggestionId}-${index}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold">{entry.normalizedName}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{entry.source}</p>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                              Original input: {entry.originalInput}
                            </p>
                          </div>
                          <div className="grid shrink-0 grid-cols-2 overflow-hidden rounded-md border border-border bg-background">
                            <Button
                              aria-pressed={decision === 'accepted'}
                              className="h-8 rounded-none border-0 px-2"
                              onClick={() =>
                                activeDraftReviewKey
                                  ? updateDraftReviewEntryDecision(
                                      activeDraftReviewKey,
                                      index,
                                      'accepted',
                                    )
                                  : undefined
                              }
                              size="sm"
                              type="button"
                              variant={decision === 'accepted' ? 'default' : 'outline'}
                            >
                              <Check className="h-3.5 w-3.5" />
                              Accept
                            </Button>
                            <Button
                              aria-pressed={decision === 'rejected'}
                              className="h-8 rounded-none border-0 px-2"
                              onClick={() =>
                                activeDraftReviewKey
                                  ? updateDraftReviewEntryDecision(
                                      activeDraftReviewKey,
                                      index,
                                      'rejected',
                                    )
                                  : undefined
                              }
                              size="sm"
                              type="button"
                              variant={decision === 'rejected' ? 'secondary' : 'outline'}
                            >
                              <X className="h-3.5 w-3.5" />
                              Reject
                            </Button>
                          </div>
                        </div>
                        <p
                          className={`text-xs ${
                            isRejected
                              ? 'text-muted-foreground'
                              : entry.requiresReview
                                ? 'text-amber-800'
                                : 'text-muted-foreground'
                          }`}
                        >
                          {isRejected
                            ? 'Rejected'
                            : entry.requiresReview
                              ? 'Needs review'
                              : 'Ready'}
                        </p>

                        <label className="grid gap-2 text-sm">
                          <span className="font-medium">Food</span>
                          <input
                            className={fieldClassName}
                            disabled={isRejected}
                            onChange={(event) =>
                              activeDraftReviewKey
                                ? updateDraftReviewEntryField(
                                    activeDraftReviewKey,
                                    index,
                                    'normalizedName',
                                    event.target.value,
                                  )
                                : undefined
                            }
                            value={reviewEntry?.normalizedName ?? entry.normalizedName}
                          />
                        </label>

                        <div className="grid grid-cols-[1fr_1.2fr] gap-3">
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Amount</span>
                            <input
                              className={fieldClassName}
                              disabled={isRejected}
                              inputMode="decimal"
                              onChange={(event) =>
                                activeDraftReviewKey
                                  ? updateDraftReviewEntryField(
                                      activeDraftReviewKey,
                                      index,
                                      'quantityAmount',
                                      event.target.value,
                                    )
                                  : undefined
                              }
                              value={reviewEntry?.quantityAmount ?? `${entry.inputQuantity.amount}`}
                            />
                          </label>
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Unit</span>
                            <input
                              className={fieldClassName}
                              disabled={isRejected}
                              onChange={(event) =>
                                activeDraftReviewKey
                                  ? updateDraftReviewEntryField(
                                      activeDraftReviewKey,
                                      index,
                                      'quantityUnit',
                                      event.target.value,
                                    )
                                  : undefined
                              }
                              value={reviewEntry?.quantityUnit ?? entry.inputQuantity.unit}
                            />
                          </label>
                        </div>

                        {logMode === 'food' ? (
                          <label className="grid gap-2 text-sm">
                            <span className="font-medium">Meal</span>
                            <select
                              className={fieldClassName}
                              disabled={isRejected}
                              onChange={(event) =>
                                activeDraftReviewKey
                                  ? updateDraftReviewEntryField(
                                      activeDraftReviewKey,
                                      index,
                                      'mealType',
                                      event.target.value as MealType,
                                    )
                                  : undefined
                              }
                              value={reviewEntry?.mealType ?? entry.mealType}
                            >
                              {mealTypes.map((mealType) => (
                                <option key={mealType} value={mealType}>
                                  {formatMealTypeLabel(mealType)}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}

                        <div className="grid grid-cols-2 gap-3">
                          {[
                            ['calories', 'Calories'],
                            ['proteinGrams', 'Protein'],
                            ['carbsGrams', 'Carbs'],
                            ['fatGrams', 'Fat'],
                          ].map(([key, label]) => (
                            <label className="grid gap-2 text-sm" key={key}>
                              <span className="font-medium">{label}</span>
                              <input
                                className={fieldClassName}
                                disabled={isRejected}
                                inputMode="decimal"
                                onChange={(event) =>
                                  activeDraftReviewKey
                                    ? updateDraftReviewEntryField(
                                        activeDraftReviewKey,
                                        index,
                                        key as keyof DraftReviewEntryState,
                                        event.target.value,
                                      )
                                    : undefined
                                }
                                value={
                                  reviewEntry?.[key as keyof DraftReviewEntryState] ??
                                  `${entry.macros[key as keyof typeof entry.macros] ?? ''}`
                                }
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {activeDraftSuggestion.draftEntries.length > 0 ? (
                <div className="grid gap-2">
                  <p className="text-xs text-muted-foreground">
                    {activeAcceptedDraftCount} of {activeDraftSuggestion.draftEntries.length}{' '}
                    {logMode === 'recipe'
                      ? activeDraftSuggestion.draftEntries.length === 1
                        ? 'ingredient'
                        : 'ingredients'
                      : activeDraftSuggestion.draftEntries.length === 1
                        ? 'food'
                        : 'foods'}{' '}
                    accepted.
                  </p>
                  {submitError ? (
                    <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {submitError}
                    </p>
                  ) : null}
                  <Button
                    className="w-full"
                    disabled={
                      confirmDraft.isPending ||
                      saveRecipe.isPending ||
                      activeAcceptedDraftCount === 0
                    }
                    onClick={() =>
                      logMode === 'recipe' ? void handleSaveRecipe() : void handleConfirmDraft()
                    }
                    type="button"
                  >
                    {confirmDraft.isPending || saveRecipe.isPending ? (
                      <>
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                        Saving
                      </>
                    ) : logMode === 'recipe' ? (
                      'Save accepted ingredients'
                    ) : (
                      'Save accepted foods'
                    )}
                  </Button>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {isManualEntryOpen ? (
        <div
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end bg-foreground/30 manual-sheet-backdrop"
          role="dialog"
        >
          <button
            aria-label="Close manual entry"
            className="absolute inset-0 h-full w-full cursor-default"
            onClick={() => setIsManualEntryOpen(false)}
            type="button"
          />
          <section className="manual-sheet-panel relative max-h-[88svh] w-full overflow-y-auto rounded-t-2xl border border-border bg-card px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-3 shadow-[0_-12px_40px_rgba(0,0,0,0.18)]">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-border" />
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Manual</p>
                <h3 className="mt-1 text-xl font-semibold">Quick add</h3>
              </div>
              <Button
                aria-label="Close manual entry"
                onClick={() => setIsManualEntryOpen(false)}
                size="icon"
                type="button"
                variant="outline"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form className="space-y-3" onSubmit={handleManualEntrySubmit}>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Meal</span>
                <select
                  className={fieldClassName}
                  onChange={(event) => updateFormField('mealType', event.target.value as MealType)}
                  value={formState.mealType}
                >
                  {mealTypes.map((mealType) => (
                    <option key={mealType} value={mealType}>
                      {formatMealTypeLabel(mealType)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid gap-2 text-sm">
                <span className="font-medium">Food</span>
                <input
                  className={fieldClassName}
                  onChange={(event) => updateFormField('normalizedName', event.target.value)}
                  placeholder="Greek yogurt"
                  required
                  type="text"
                  value={formState.normalizedName}
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Quantity</span>
                  <input
                    className={fieldClassName}
                    min="0.1"
                    onChange={(event) => updateFormField('quantityAmount', event.target.value)}
                    required
                    step="0.1"
                    type="number"
                    value={formState.quantityAmount}
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Unit</span>
                  <input
                    className={fieldClassName}
                    onChange={(event) => updateFormField('quantityUnit', event.target.value)}
                    placeholder="cup"
                    required
                    type="text"
                    value={formState.quantityUnit}
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Calories</span>
                  <input
                    className={fieldClassName}
                    min="0"
                    onChange={(event) => updateFormField('calories', event.target.value)}
                    required
                    step="1"
                    type="number"
                    value={formState.calories}
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Protein (g)</span>
                  <input
                    className={fieldClassName}
                    min="0"
                    onChange={(event) => updateFormField('proteinGrams', event.target.value)}
                    required
                    step="0.1"
                    type="number"
                    value={formState.proteinGrams}
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Carbs (g)</span>
                  <input
                    className={fieldClassName}
                    min="0"
                    onChange={(event) => updateFormField('carbsGrams', event.target.value)}
                    required
                    step="0.1"
                    type="number"
                    value={formState.carbsGrams}
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Fat (g)</span>
                  <input
                    className={fieldClassName}
                    min="0"
                    onChange={(event) => updateFormField('fatGrams', event.target.value)}
                    required
                    step="0.1"
                    type="number"
                    value={formState.fatGrams}
                  />
                </label>
              </div>

              {submitError ? (
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {submitError}
                </p>
              ) : null}

              {submitSuccess ? (
                <p className="rounded-md border border-border bg-secondary px-3 py-2 text-sm text-foreground">
                  {submitSuccess}
                </p>
              ) : null}

              <Button
                className="w-full"
                disabled={createManualEntry.isPending}
                size="lg"
                type="submit"
              >
                {createManualEntry.isPending ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  'Save food'
                )}
              </Button>
            </form>
          </section>
        </div>
      ) : null}

      {isRecentChatsOpen ? (
        <div
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end bg-foreground/30 manual-sheet-backdrop"
          role="dialog"
        >
          <button
            aria-label="Close recent chats"
            className="absolute inset-0 h-full w-full cursor-default"
            onClick={() => setIsRecentChatsOpen(false)}
            type="button"
          />
          <section className="manual-sheet-panel relative max-h-[88svh] w-full overflow-hidden rounded-t-2xl border border-border bg-card px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-3 shadow-[0_-12px_40px_rgba(0,0,0,0.18)]">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-border" />
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Chat</p>
                <h3 className="mt-1 text-xl font-semibold">Recent</h3>
              </div>
              <Button
                aria-label="Close recent chats"
                onClick={() => setIsRecentChatsOpen(false)}
                size="icon"
                type="button"
                variant="outline"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="max-h-[68svh] overflow-y-auto pb-2">
              {recentSessions.length > 0 ? (
                <div className="divide-y divide-border border-y border-border">
                  {recentSessions.map((session) => {
                    const isActive = session.id === activeSession?.id;
                    const messageCount = getSessionMessageCount(session);

                    return (
                      <button
                        className={`grid w-full gap-1 px-1 py-3 text-left transition-colors ${
                          isActive
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        key={session.id}
                        onClick={() => navigateToSession(session.id)}
                        type="button"
                      >
                        <span className="flex items-start justify-between gap-3">
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-foreground">
                              {session.title}
                            </span>
                            <span className="mt-1 block truncate text-xs">
                              {getSessionPreview(session)}
                            </span>
                          </span>
                          <span className="shrink-0 text-right text-[11px] leading-4 text-muted-foreground">
                            {formatSessionUpdatedAt(session.updatedAt)}
                          </span>
                        </span>
                        <span className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
                          <span>
                            {messageCount} message{messageCount === 1 ? '' : 's'}
                          </span>
                          {isActive ? (
                            <span className="font-medium text-foreground">Open</span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-md border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                  No recent chats yet.
                </p>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
